/** The only authored CAIRE palette. CSS and non-browser artifacts derive from here. */
const shared = {
  fonts: {
    sans: '"Schibsted Grotesk", system-ui, -apple-system, "Segoe UI", sans-serif',
    display: '"Schibsted Grotesk", system-ui, sans-serif',
    serif: '"Fraunces", Georgia, "Times New Roman", serif',
    mono: '"Geist Mono", "SF Mono", Menlo, Consolas, monospace',
    email:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  assets: {
    websiteIcon: "/favicons/favicon-caire-v2.svg",
    dashboardIcon: "/favicons/favicon-dashboard-v2.svg",
    emailLogo: "https://www.caire.se/favicons/favicon-caire-v2-192x192.png",
  },
};

const ai = {
  aiCyan: "#22d3ee",
  aiBlue: "#6366f1",
  aiMagenta: "#d946ef",
  aiText: "#4f46b8",
  aiCyanSoft: "#cffafe",
  aiBlueSoft: "#ddd6fe",
  aiMagentaSoft: "#fae8ff",
};

const signature = {
  id: "signature",
  label: "Signature",
  ...shared,
  colors: {
    ink: "#0e1620",
    navy: "#1c2638",
    inkWell: "#0b121a",
    inkStrong: "#05080c",
    page: "#fbf9f4",
    sand: "#f4efe6",
    sandDeep: "#ede5d5",
    surface: "#ffffff",
    surfaceWarm: "#fffaf2",
    rail: "#fffdf9",
    muted: "#465266",
    slate: "#536072",
    warm: "#6c6457",
    faint: "#6b6455",
    trace: "#78716c",
    border: "#e6ddcf",
    borderSoft: "#ece7da",
    borderStrong: "#d9cebf",
    borderSand: "#ddd3c2",
    glyph: "#c9bfae",
    accent: "#e8714a",
    accentDeep: "#c85a36",
    accentSoft: "#f4a574",
    accentPale: "#f4d8ca",
    accentTint: "#fdeee6",
    accentOn: "#5c2310",
    action: "#a13f20",
    actionHover: "#853217",
    onAction: "#ffffff",
    ...ai,
  },
};

export const presets = {
  signature,
  care: {
    ...signature,
    id: "care",
    label: "Care",
    colors: {
      ...signature.colors,
      action: "#155e63",
      actionHover: "#10494d",
      onAction: "#ffffff",
      accent: "#438b86",
      accentDeep: "#155e63",
      accentSoft: "#a6d2c9",
      accentPale: "#d0e8e1",
      accentTint: "#e3f0ec",
      accentOn: "#10494d",
    },
  },
  nordic: {
    id: "nordic",
    label: "Nordic",
    ...shared,
    colors: {
      ink: "#0f172a",
      navy: "#1e293b",
      inkWell: "#0b1120",
      inkStrong: "#020617",
      page: "#f8fafc",
      sand: "#f1f5f9",
      sandDeep: "#e2e8f0",
      surface: "#ffffff",
      surfaceWarm: "#f8fafc",
      rail: "#f8fafc",
      muted: "#475569",
      slate: "#475569",
      warm: "#475569",
      faint: "#475569",
      trace: "#64748b",
      border: "#cbd5e1",
      borderSoft: "#e2e8f0",
      borderStrong: "#94a3b8",
      borderSand: "#cbd5e1",
      glyph: "#94a3b8",
      accent: "#0284c7",
      accentDeep: "#0369a1",
      accentSoft: "#7dd3fc",
      accentPale: "#bae6fd",
      accentTint: "#e0f2fe",
      accentOn: "#0c4a6e",
      action: "#0369a1",
      actionHover: "#075985",
      onAction: "#ffffff",
      ...ai,
    },
  },
};

export const DEFAULT_BRAND_ID = "signature";

/**
 * Brand 2.2 domain roles: what KIND of information something is, never good or
 * bad. Same meaning on the website, in reports, email and the dashboard.
 * `tint` = card fill, `solid` = icon tile / large fill, `ink` = text on tint.
 * White text on `solid` is allowed only for planning, care, continuity and
 * society; attention and human solids carry white icons only.
 */
export const domainColors = Object.freeze({
  planning: Object.freeze({
    tint: "#e3ecf7",
    solid: "#3d6fb0",
    ink: "#1d3f6e",
  }),
  care: Object.freeze({ tint: "#e2f0e6", solid: "#367a50", ink: "#1f5236" }),
  continuity: Object.freeze({
    tint: "#ece6f6",
    solid: "#7356b0",
    ink: "#43307a",
  }),
  attention: Object.freeze({
    tint: "#f8ead2",
    solid: "#c4861f",
    ink: "#6e4a0c",
  }),
  human: Object.freeze({ tint: "#f9e2d8", solid: "#d0603b", ink: "#8a2f14" }),
  society: Object.freeze({ tint: "#e9ebe6", solid: "#152733", ink: "#152733" }),
  ai: Object.freeze({ tint: "#eef0ff", solid: "#6366f1", ink: "#3b3fa8" }),
});

/** Domains whose solid fill may carry white body text (contrast-tested). */
export const domainsWithWhiteText = Object.freeze([
  "planning",
  "care",
  "continuity",
  "society",
]);

/** Handwritten annotation layer (HandNote). Website only. */
export const handFont = '"Caveat", "Segoe Print", cursive';

/**
 * Semantic report areas shared by screen, PDF, Excel and email; not brand
 * identity or status. Brand 2.2: derived from the domain roles, never authored
 * here. The area colour is the domain `ink` (text, ≥4.5:1 on its surface) and
 * the surface is the domain `tint`; bars, chart series and Excel fills use
 * `reportFills` (the domain `solid`).
 */
const reportAreas = ["planning", "care", "continuity", "attention"];

export const reportColors = Object.freeze(
  Object.fromEntries(
    reportAreas.flatMap((area) => [
      [area, domainColors[area].ink],
      [`${area}Surface`, domainColors[area].tint],
    ]),
  ),
);

/** Report fills (bars, chart series, Excel series): the domain `solid`. */
export const reportFills = Object.freeze({
  ...Object.fromEntries(
    reportAreas.map((area) => [area, domainColors[area].solid]),
  ),
  /** Work that needs action (very late, unassigned): the human domain. */
  human: domainColors.human.solid,
});

/**
 * Status and severity tones as hex, for consumers that cannot read a CSS
 * variable (react-pdf, Excel, canvas, map paint, Clerk appearance). Each value
 * is the exact conversion of the matching `--severity-*` / `--status-*` HSL in
 * packages/ui/themes/dashboard.css; status-colors.test.mjs keeps them equal.
 * In the browser prefer the token classes (`text-severity-ok`,
 * `bg-status-failed`) or `hsl(var(--severity-ok))`.
 */
export const statusColors = Object.freeze({
  critical: "#d65d00",
  warning: "#e6a100",
  info: "#58b4e9",
  ok: "#009e74",
  completed: "#21c45d",
  failed: "#ef4343",
  active: "#3c83f6",
  draft: "#f59f0a",
  accepted: "#3c83f6",
  planned: "#6b26d9",
  published: "#21c45d",
  archived: "#6b7280",
});
